import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Vega } from 'react-vega';
import clsx from 'clsx';
import { Error as LogLevelError, scheme, type View } from 'vega';

import { type ChatChartRecipe, chatChartRecipeKey } from './chatChartRecipe';
import {
  type ChatChartColors,
  chatChartSize,
  createChatChartSpec,
} from './chatChartSpec';
import { useChatChartWidth } from './useChatChartWidth';

import styles from './ChatChart.module.scss';

const FALLBACK_PALETTE = [
  '#0D161F',
  '#FC641C',
  '#104153',
  '#89B0D4',
  '#FFB661',
  '#E4E4E5',
  '#48484C',
  '#DD5A26',
  '#DDAF26',
  '#B7D526',
  '#1A9862',
];

const FALLBACK_COLORS: ChatChartColors = {
  font: 'Inter, sans-serif',
  grid: 'rgba(7, 23, 32, 0.15)',
  mark: 'rgb(31, 101, 141)',
  palette: FALLBACK_PALETTE,
  text: 'rgb(7, 23, 32)',
};

function themeColorSource(): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null;
  }

  return document.body ?? document.documentElement;
}

function readTenantPalette(): string[] {
  const registered = scheme('AssetMetrix12');
  if (!Array.isArray(registered)) {
    return FALLBACK_PALETTE;
  }

  const palette = registered.filter(
    (color): color is string => typeof color === 'string' && color !== ''
  );
  return palette.length > 0 ? palette : FALLBACK_PALETTE;
}

function readThemeColors(): ChatChartColors {
  const source = themeColorSource();
  if (source == null) {
    return FALLBACK_COLORS;
  }

  const styles = getComputedStyle(source);
  const text = styles.getPropertyValue('--portal-ui-color-text').trim();
  const primary = styles.getPropertyValue('--portal-ui-color-primary').trim();
  const font =
    styles.getPropertyValue('--portal-ui-font-family').trim() ||
    styles.fontFamily.trim();

  return {
    font: font !== '' ? font : FALLBACK_COLORS.font,
    grid: text !== '' ? `rgba(${text}, 0.15)` : FALLBACK_COLORS.grid,
    mark: primary !== '' ? `rgb(${primary})` : FALLBACK_COLORS.mark,
    palette: readTenantPalette(),
    text: text !== '' ? `rgb(${text})` : FALLBACK_COLORS.text,
  };
}

type ChatChartProps = {
  recipe: ChatChartRecipe;
};

type ChartTooltip = {
  left: number;
  text: string;
  top: number;
};

export function chatChartTooltipText(value: unknown): string | null {
  if (typeof value === 'string') {
    return value === '' ? null : value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function sameThemeColors(
  left: ChatChartColors,
  right: ChatChartColors
): boolean {
  return (
    left.font === right.font &&
    left.grid === right.grid &&
    left.mark === right.mark &&
    left.text === right.text &&
    left.palette.join('\0') === right.palette.join('\0')
  );
}

function ChatChartComponent({ recipe }: ChatChartProps) {
  const hostRef = useRef<HTMLElement>(null);
  const [colors, setColors] = useState(readThemeColors);
  const [tooltip, setTooltip] = useState<ChartTooltip | null>(null);
  const width = useChatChartWidth(hostRef);
  const recipeKey = chatChartRecipeKey(recipe);
  const size = useMemo(
    () => chatChartSize(recipe, width || undefined),
    // recipeKey is the content identity; `recipe` is read inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recipeKey, width]
  );
  // Spec identity follows recipe contents and theme. Measured size rides
  // Vega's width/height props so a panel drag resizes the live view.
  const spec = useMemo(
    () => createChatChartSpec(recipe, colors),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [colors, recipeKey]
  );
  const handleError = useCallback((error: Error) => {
    console.error(error);
  }, []);
  const handleNewView = useCallback((view: View) => {
    view.tooltip((_handler, event, _item, value) => {
      const text = chatChartTooltipText(value);
      const host = hostRef.current;
      if (text == null || host == null || event == null) {
        setTooltip(null);
        return;
      }

      const rect = host.getBoundingClientRect();
      setTooltip({
        left: event.clientX - rect.left,
        text,
        top: event.clientY - rect.top,
      });
    });
  }, []);

  useEffect(() => {
    const source = themeColorSource();
    if (source == null) {
      return;
    }

    const syncColors = () => {
      setColors((current) => {
        const next = readThemeColors();
        return sameThemeColors(current, next) ? current : next;
      });
    };

    syncColors();
    const observer = new MutationObserver(syncColors);
    observer.observe(source, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
    return () => observer.disconnect();
  }, []);

  return (
    <figure
      ref={hostRef}
      className={clsx(styles['chart'], {
        [styles['chart--donut']]: recipe.type === 'donut',
      })}
      data-testid={`chat-chart-${recipe.type}`}
    >
      {recipe.title != null ? (
        <figcaption className={styles['title']}>{recipe.title}</figcaption>
      ) : null}
      <Vega
        actions={false}
        height={size.height}
        logLevel={LogLevelError}
        onError={handleError}
        onNewView={handleNewView}
        renderer="svg"
        spec={spec}
        tooltip={false}
        width={size.width}
      />
      {tooltip != null ? (
        <div
          className={styles['tooltip']}
          role="tooltip"
          style={{ left: tooltip.left, top: tooltip.top }}
        >
          {tooltip.text}
        </div>
      ) : null}
    </figure>
  );
}

/** Skip parent re-renders when the recipe contents did not change. */
export const ChatChart = memo(ChatChartComponent, (previous, next) => {
  return chatChartRecipeKey(previous.recipe) === chatChartRecipeKey(next.recipe);
});
